import { useState, useEffect } from 'react';
import type { CalculatorConfig } from '@shared/types';
import {
  trackCalculatorStarted,
  trackCalculatorStepCompleted,
  trackCalculatorCompleted,
} from '@shared/analytics';
import StapJouwAuto from './StapJouwAuto';
import type { StapJouwAutoState } from './StapJouwAuto';
import StapRijgedrag from './StapRijgedrag';
import type { StapRijgedragState } from './StapRijgedrag';
import StapLaadvoorkeur from './StapLaadvoorkeur';
import type { StapLaadvoorkeurState } from './StapLaadvoorkeur';
import StapLeadGate from './StapLeadGate';
import StapResultaat from './StapResultaat';
import { berekenAdvies } from './berekenAdvies';
import type { CalculatorResultaat } from './berekenAdvies';
import { AUTO_FALLBACK } from './autoDatabase';

const STAP_LABELS = ['Jouw auto', 'Rijgedrag', 'Laadvoorkeur', 'Resultaat'] as const;
const TOTAL_STAPPEN = STAP_LABELS.length;

const LS_KEY = 'laadcheck_calculator_state';

interface Props {
  config: CalculatorConfig;
}

/**
 * Step numbering:
 *   1 — Jouw auto (vraag 1)
 *   2 — Rijgedrag  (vraag 2)
 *   3 — Laadvoorkeur (vraag 3)
 *   4 — Lead gate   (email capture, VOOR uitslag — THU-213)
 *   5 — Resultaat   (uitslag)
 *
 * Progress indicator is shown for steps 1–3 only.
 */
export default function App({ config }: Props) {
  const [huidigeStap, setHuidigeStap] = useState<number>(1);

  // Track calculator opened (once on mount)
  useEffect(() => {
    trackCalculatorStarted();
  }, []);

  const [stapAuto, setStapAuto] = useState<StapJouwAutoState>({
    heeftEV: null,
    autoModel: null,
    categorie: null,
    orientatieType: null,
  });

  const [stapRijgedrag, setStapRijgedrag] = useState<StapRijgedragState>({
    kmPerDag: 40,
    kmPiekDag: 60,
    dagenPerWeek: 5,
    langeRitten: false,
    aantalEvs: 1,
  });

  const [stapLaadvoorkeur, setStapLaadvoorkeur] = useState<StapLaadvoorkeurState>({
    laadMoment: null,
    beschikbareUren: 8,
    laadtElders: 'nee',
    zonnepanelen: false,
    aansluitingType: null,
    zwareVerbruikers: { warmtepomp: false, inductie: false, airco: false, boiler: false },
  });

  const [resultaat, setResultaat] = useState<CalculatorResultaat | null>(null);

  function berekenEnToonLeadGate() {
    const autoModel = stapAuto.autoModel ?? AUTO_FALLBACK;

    const input = {
      autoModel,
      kmPerDag: stapRijgedrag.kmPerDag,
      kmPiekDag: stapRijgedrag.kmPiekDag,
      dagenPerWeek: stapRijgedrag.dagenPerWeek,
      langeRitten: stapRijgedrag.langeRitten,
      aantalEvs: stapRijgedrag.aantalEvs,
      laadMoment: stapLaadvoorkeur.laadMoment!,
      beschikbareUren: stapLaadvoorkeur.beschikbareUren,
      laadtElders: stapLaadvoorkeur.laadtElders,
      zonnepanelen: stapLaadvoorkeur.zonnepanelen,
      aansluitingType: stapLaadvoorkeur.aansluitingType,
    };

    const res = berekenAdvies(input);
    setResultaat(res);

    // Persist to localStorage for transfer to assessment wizard
    const snapshot = { stapAuto, stapRijgedrag, stapLaadvoorkeur, resultaat: res };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(snapshot));
    } catch {
      // localStorage may be unavailable (incognito etc.) — silently ignore
    }

    trackCalculatorStepCompleted(3, 'laadvoorkeur');
    trackCalculatorCompleted(res.aanbevolenKw, res.adviesType);

    // Go to lead gate (step 4) BEFORE showing the resultaat (step 5)
    setHuidigeStap(4);
  }

  function handleLeadGateSubmit(_email: string) {
    // email is captured; proceed to show the uitslag
    setHuidigeStap(5);
  }

  return (
    <div className="laadcheck-calculator">
      {/* Progress indicator — visible only during question steps 1–3 */}
      {huidigeStap < 4 && (
        <div
          className="lc-voortgang"
          role="progressbar"
          aria-valuenow={huidigeStap}
          aria-valuemax={TOTAL_STAPPEN}
        >
          {STAP_LABELS.map((label, i) => {
            const stapNr = i + 1;
            const isActief = stapNr === huidigeStap;
            const isKlaar = stapNr < huidigeStap;
            return (
              <div
                key={label}
                className={[
                  'lc-voortgang__stap',
                  isActief ? 'lc-voortgang__stap--actief' : '',
                  isKlaar ? 'lc-voortgang__stap--klaar' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="lc-voortgang__nummer">{isKlaar ? '✓' : stapNr}</span>
                <span className="lc-voortgang__label">{label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Steps */}
      {huidigeStap === 1 && (
        <StapJouwAuto
          state={stapAuto}
          onChange={setStapAuto}
          onNext={() => {
            trackCalculatorStepCompleted(1, 'auto_selectie');
            setHuidigeStap(2);
          }}
        />
      )}

      {huidigeStap === 2 && (
        <StapRijgedrag
          state={stapRijgedrag}
          onChange={setStapRijgedrag}
          onNext={() => {
            trackCalculatorStepCompleted(2, 'rijgedrag');
            setHuidigeStap(3);
          }}
          onBack={() => setHuidigeStap(1)}
          heeftEV={stapAuto.heeftEV}
        />
      )}

      {huidigeStap === 3 && (
        <StapLaadvoorkeur
          state={stapLaadvoorkeur}
          onChange={setStapLaadvoorkeur}
          onNext={berekenEnToonLeadGate}
          onBack={() => setHuidigeStap(2)}
          heeftEV={stapAuto.heeftEV}
        />
      )}

      {/* Lead gate: email capture BEFORE showing uitslag (THU-213) */}
      {huidigeStap === 4 && resultaat !== null && (
        <StapLeadGate
          config={config}
          resultaat={resultaat}
          stapAuto={stapAuto}
          stapRijgedrag={stapRijgedrag}
          onSubmit={handleLeadGateSubmit}
        />
      )}

      {/* Uitslag: shown only after lead gate is submitted */}
      {huidigeStap === 5 && resultaat !== null && (
        <StapResultaat
          resultaat={resultaat}
          assessmentUrl={config.assessmentUrl}
          ajaxUrl={config.ajaxUrl}
          nonce={config.nonce}
          zwareVerbruikers={stapLaadvoorkeur.zwareVerbruikers}
          beschikbareUren={stapLaadvoorkeur.beschikbareUren}
          onTerug={() => setHuidigeStap(3)}
        />
      )}
    </div>
  );
}
